import { useEffect } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { compressImage } from '@/lib/imageCompression';
import { markdownToHtml, isMarkdownFile, isMarkdownContent } from '@/lib/markdownUtils';

interface UseWallPasteProps {
  targetBoardId: string | null;
  onItemCreated?: () => void;
}

export function useWallPaste({ targetBoardId, onItemCreated }: UseWallPasteProps) {
  const { createItem } = useCanvasStore();

  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      // Don't capture if user is typing in an input
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      e.preventDefault();

      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error('Please sign in to add items');
        return;
      }

      // Handle files first (images, videos, documents, markdown)
      if (clipboardData.files && clipboardData.files.length > 0) {
        e.preventDefault();
        for (const file of Array.from(clipboardData.files)) {
          // Check if it's a markdown file - read and convert to text card
          if (isMarkdownFile(file.name)) {
            const text = await file.text();
            await handleMarkdownPaste(text, file.name);
          } else {
            await handleFilePaste(file, session.user.id);
          }
        }
        return;
      }

      // FIX 1: Check clipboard items for images (screenshots)
      if (clipboardData.items) {
        for (const item of Array.from(clipboardData.items)) {
          if (item.type.startsWith('image/')) {
            e.preventDefault();
            const file = item.getAsFile();
            if (file) {
              await handleFilePaste(file, session.user.id);
            }
            return;
          }
        }
      }

      // Handle text/URLs
      const text = clipboardData.getData('text/plain');
      if (text && text.trim()) {
        e.preventDefault();
        await handleTextPaste(text.trim());
      }
    };

    const handleFilePaste = async (file: File, userId: string) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      try {
        let fileToUpload: File = file;
        
        // Compress images before upload
        if (isImage && !file.type.includes('gif') && !file.type.includes('svg')) {
          const result = await compressImage(file);
          fileToUpload = result.file;
        }

        // Upload file to storage
        const filename = `${userId}/${Date.now()}_${file.name}`;
        const { error } = await supabase.storage
          .from('uploads')
          .upload(filename, fileToUpload);

        if (error) {
          toast.error('Failed to upload file');
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('uploads')
          .getPublicUrl(filename);

        // Create item - explicitly pass null for board_id for Wall items without a board
        if (isImage) {
          await createItem({
            board_id: targetBoardId, // Will be null if no board selected
            item_type: 'image',
            position_x: Math.random() * 400,
            position_y: Math.random() * 400,
            width: 280,
            height: 200,
            content: {
              url: publicUrl,
              filename: file.name,
              alt: file.name,
              needsBoard: !targetBoardId,
            }
          });
        } else if (isVideo) {
          await createItem({
            board_id: targetBoardId,
            item_type: 'video',
            position_x: Math.random() * 400,
            position_y: Math.random() * 400,
            width: 320,
            height: 240,
            content: {
              videoUrl: publicUrl,
              filename: file.name,
              needsBoard: !targetBoardId,
            }
          });
        } else {
          await createItem({
            board_id: targetBoardId,
            item_type: 'file',
            position_x: Math.random() * 400,
            position_y: Math.random() * 400,
            width: 280,
            height: 80,
            content: {
              url: publicUrl,
              filename: file.name,
              size: file.size,
              mimeType: file.type,
              needsBoard: !targetBoardId,
            }
          });
        }

        toast.success('Item added to Wall');
        onItemCreated?.();
      } catch (err) {
        console.error('Paste error:', err);
        toast.error('Failed to paste item');
      }
    };

    const handleMarkdownPaste = async (text: string, filename?: string) => {
      try {
        const htmlContent = markdownToHtml(text);
        const isLongContent = text.length > 500;
        const title = filename?.replace(/\.(md|markdown|mdown|mkd|mkdn)$/i, '') || '';
        
        await createItem({
          board_id: targetBoardId,
          item_type: isLongContent ? 'document' : 'text_card',
          position_x: Math.random() * 400,
          position_y: Math.random() * 400,
          width: isLongContent ? 280 : 320,
          height: isLongContent ? 120 : 400,
          content: {
            body: htmlContent,
            title,
            needsBoard: !targetBoardId,
          }
        });

        toast.success(`${isLongContent ? 'Document' : 'Markdown'} added to Wall`);
        onItemCreated?.();
      } catch (err) {
        console.error('Markdown paste error:', err);
        toast.error('Failed to paste markdown');
      }
    };

    const handleTextPaste = async (text: string) => {
      try {
        // Check if it's a URL
        const urlPattern = /^(https?:\/\/[^\s]+)$/;
        const isUrl = urlPattern.test(text.trim());

        if (isUrl) {
          // Fetch link metadata and create link card
          const { data: metadata } = await supabase.functions.invoke('fetch-link-metadata', {
            body: { url: text.trim() }
          });

          await createItem({
            board_id: targetBoardId,
            item_type: 'link',
            position_x: Math.random() * 400,
            position_y: Math.random() * 400,
            width: 280,
            height: 200,
            content: {
              url: text.trim(),
              title: metadata?.title || text,
              description: metadata?.description || '',
              image: metadata?.image || null,
              siteName: metadata?.siteName || '',
              favicon: metadata?.favicon || null,
              imageAspectRatio: metadata?.imageAspectRatio || null,
              contentType: metadata?.contentType || 'website',
              price: metadata?.price || null,
              screenshot: metadata?.screenshot || null,
              needsBoard: !targetBoardId,
            }
          });
        } else if (isMarkdownContent(text)) {
          // Detected markdown content - convert to HTML
          await handleMarkdownPaste(text);
        } else {
          // Create text card or sticky based on length
          const isShort = text.length < 200;

          await createItem({
            board_id: targetBoardId,
            item_type: isShort ? 'sticky' : 'text_card',
            position_x: Math.random() * 400,
            position_y: Math.random() * 400,
            width: 280,
            height: isShort ? 200 : 300,
            content: isShort
              ? { text: text, color: 'yellow', needsBoard: !targetBoardId }
              : { body: text, needsBoard: !targetBoardId }
          });
        }

        toast.success('Item added to Wall');
        onItemCreated?.();
      } catch (err) {
        console.error('Paste error:', err);
        toast.error('Failed to paste item');
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [targetBoardId, createItem, onItemCreated]);
}
